import { MapPinOff, UtensilsCrossed } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface EmptyHomeFeedAction {
  label: string;
  onClick: () => void;
}

interface EmptyHomeFeedProps {
  reason: 'no-locale' | 'no-listings';
  action?: EmptyHomeFeedAction;
}

export const EmptyHomeFeed: React.FC<EmptyHomeFeedProps> = ({ reason, action }) => {
  // Strings live in the `home` namespace, lazy-loaded by i18next-http-backend
  // on first render of this component. `action.label` is caller-provided —
  // the calling screen is responsible for translating it (so the verb can
  // vary by context: "Refresh", "Try again", "Become a chef", etc.).
  const { t } = useTranslation('home');

  if (reason === 'no-locale') {
    return (
      <div className="flex flex-col items-center text-center px-4 pb-16 pt-6 space-y-4">
        <div className="rounded-full bg-muted p-4">
          <MapPinOff className="h-10 w-10 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium">{t('feed.empty.noLocale.title')}</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          {t('feed.empty.noLocale.body')}
        </p>
      </div>
    );
  }

  return (
    // Less bottom padding than the standalone no-locale state: this branch is
    // followed by the chef CTA + ShareAppCard, so a large pb here just opens an
    // awkward gap before those cards (worse at large text).
    <div className="flex flex-col items-center text-center px-4 pb-6 pt-6 space-y-4">
      <div className="rounded-full bg-muted p-4">
        <UtensilsCrossed className="h-10 w-10 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-medium">{t('feed.empty.noListings.title')}</h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        {t('feed.empty.noListings.body')}
      </p>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          // Fixed radius = half the one-line height: stays pill-shaped on a
          // single line, but wraps to normal rounded corners with straight
          // sides at large text instead of mega-rounded semicircle ends.
          className="rounded-3xl bg-[var(--ion-color-primary)] px-6 py-3 text-white font-semibold shadow-sm active:scale-[0.99] transition-transform"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};
